import { PriceFunc, PriceElemsData, PriceSegment, getListPrice } from '@icc/price';
import { CustomPricesRecords } from '@icc/shared/custom-price/CustomPrice';
import { PriceAccessoryService } from '@icc/construction-elements/configurator';
import { Injectable, Inject } from '@angular/core';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { Common } from '@icc/shared/helpers';
import { core } from '@icc/shared/helpers';
import { IccAccessoryAccessory } from '@icc/construction-elements/types';
import { TranslateService } from '@icc/shared/translate.service';
import { Components } from '@icc/window-door/types';

/* eslint-disable no-console */



/**
 * Factory dopłat za okucie.
 *
 * @export
 * @param {object} Core Core
 * @return {object} Factory
 */
@Injectable()
export class PriceFittingService {
    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private priceAccessoryService: PriceAccessoryService,
        private translateService: TranslateService
    ) {}
    /**
     * Dolicza dopłatę procentową za okucie bezpieczne.
     *
     * @param {number} price         Wejsciowa cena
     * @param {object} PriceElems    Składowe wyceny
     * @param {array}  NoPriceCauses Powody braku wyceny
     * @param {array}  constrElems   Lista elementów konstrukcyjnych, do przeliczenia przez dopłatę.
     * @param {object} fitting       Okucie bezpieczne
     * @return {number} Cena z doliczoną dopłatą.
     */
    @PriceFunc({
        shortName: 'fittingPercent',
        data: {
            fitting: 'conf.Fitting',
            system: 'conf.System',
            customPrice: 'price.WindowFitting',
            components: 'conf.Components',
        },
    })
    suppFittingPercent(
        { listPrice }: PriceElemsData,
        { fitting, system, customPrice, components }: { fitting; system; customPrice: CustomPricesRecords, components: Components }
    ): PriceSegment[] {
        let factors = 1;
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        if(components && !components.hardware) {
            return [];
        }
        if (Common.isObject(fitting) && ~~fitting.price_type === 1) {
            let fittingPrice;
            if (Common.isObject(customPrice) && customPrice[fitting.id]) {
                fittingPrice = customPrice[fitting.id].getPrice('price');
            } else {
                fittingPrice = parseFloat(getListPrice(fitting, 'price', listPrice, listPriceEnableInMarket));
            }

            factors *= (100 + fittingPrice) / 100;

            return <PriceSegment[]>[
                {
                    type: 'fittingsPercent',
                    baseValue: factors,
                    value: factors,
                    valueType: 'percent',
                    data: {
                        id: fitting.id,
                        name: fitting.name,
                    },
                },
            ];
        }
        return [];
    }

    /**
     * Dolicza dopłatę kwotową za okucie bezpieczne.
     * @param  {number} price       Wejsciowa cena
     * @param  {object} PriceElems  Wycena
     * @param  {object} fitting     Okuсie bezpieczne
     * @return {number}             Cena z doliczonym okuciem
     */
    @PriceFunc({
        shortName: 'fitting',
        data: {
            fitting: 'conf.Fitting',
            sashes: 'conf.Sashes',
            customPrice: 'price.WindowFitting',
            components: 'conf.Components',
        },
    })
    suppFitting(
        { listPrice }: PriceElemsData,
        { sashes, fitting, customPrice, components }: { sashes; fitting; customPrice: CustomPricesRecords, components: Components }
    ): PriceSegment[] {
        const priceStack: PriceSegment[] = [];
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        if(components && !components.hardware) {
            return [];
        }
        if (Common.isObject(fitting) && ~~fitting.price_type === 0) {
            for (let i = 0; i < sashes.length; i++) {
                if (
                    sashes[i].type.type !== 'F'
                    && sashes[i].type.type !== 'FF'
                    && sashes[i].type.type !== 'OFF'
                    && sashes[i].type.type !== 'OF'
                ) {
                    let fittingPrice;
                    if (Common.isObject(customPrice) && customPrice[fitting.id]) {
                        fittingPrice = customPrice[fitting.id].getPrice('price');
                    } else {
                        fittingPrice = parseFloat(getListPrice(fitting, 'price', listPrice, listPriceEnableInMarket));
                    }
                    priceStack.push({
                        type: 'fittings',
                        baseValue: fittingPrice,
                        value: fittingPrice,
                        valueType: 'value',
                        data: {
                            id: fitting.id,
                            name: fitting.name,
                        },
                    });
                }
            }
        }
        return priceStack;
    }

    /**
     * Wylicza dopłatę za zawias w danym kolorze.
     * @param  {object} handle        Klamka
     * @param  {object} PriceElems    Wycena
     * @param  {object} NoPriceCauses Powody braku ceny
     * @param  {object} handleColor   Kolor klamki
     * @return {number}               Dopłata za klamkę
     */
    @PriceFunc({
        shortName: 'hinge',
        data: {
            fitting: 'conf.Fitting',
            hinge: 'conf.Hinge',
            hingeColor: 'conf.HingeColor',
            sashes: 'conf.Sashes',
            colors: 'conf.Colors',
            confType: 'conf.type',
            components: 'conf.Components',
        },
    })
    suppHinge(
        { NoPriceCauses, listPrice }: PriceElemsData,
        { fitting, hinge, hingeColor, sashes, colors, confType, components }: {fitting; hinge; hingeColor; sashes; colors; confType; components: Components}
    ): PriceSegment[] {
        let supp = 0;
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        const hiddenFittingSelected = Number(fitting?.hidden_fitting) === 1;

        if (hiddenFittingSelected) {
            return [];
        }

        if(components && !components.hardware) {
            return [];
        }
        if (hinge) {
            if (!hinge.show_colors && !hinge.no_price) {
                supp = Number(getListPrice(hinge, 'price_white', listPrice, listPriceEnableInMarket) || 0);
                supp += Number(getListPrice(hinge, 'price_for_assembly', listPrice, listPriceEnableInMarket) || 0);
                return <PriceSegment[]>[
                    {
                        type: 'hinge',
                        baseValue: !isNaN(supp) ? supp : null,
                        value: !isNaN(supp) ? supp : null,
                        valueType: 'value',
                        data: {
                            id: hinge.id,
                            name: hinge.name,
                            colorField: this.priceAccessoryService.getAccessoryPriceField(
                                colors,
                                confType
                            ),
                        },
                    },
                ];
            } else if (hinge.colors_prices && hingeColor && hinge.colors_prices[hingeColor.id]) {
                let price = parseFloat(getListPrice(hinge, 'colors_prices', listPrice, listPriceEnableInMarket, null, hingeColor.id));
                price += parseFloat(getListPrice(hinge, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0;
                const count = sashes.reduce((prev, cur) => {
                    return (
                        prev + (['F', 'FF', 'OF'].indexOf(cur.type.type) === -1 ? 1 : 0)
                    );
                }, 0);
                return <PriceSegment[]>[
                    {
                        type: 'hinge',
                        baseValue: price * count,
                        value: price * count,
                        valueType: 'value',
                        data: {
                            id: hinge.id,
                            name: hinge?.name,
                            colorName: hingeColor?.name,
                            count,
                        },
                    },
                ];
            } else {
                if (this.config().IccConfig.Configurators.door.version === 2) {
                    NoPriceCauses.push('no hinge price');
                    return <PriceSegment[]>[
                        {
                            type: 'hinge',
                            baseValue: null,
                            value: null,
                            valueType: 'value',
                            data: {
                                id: hinge.id,
                                name: hinge?.name,
                                colorName: hingeColor?.name,
                            },
                        },
                    ];
                } else {
                    return [];
                }
            }
        } else {
            return [];
        }
    }

    /**
     * Dolicza dopłatę za zamek.
     * @param  {number} price        Cena wejściowa
     * @param  {object} PriceElems   Wycena
     * @param  {object} NoPriceElems Elementy bez wyceny
     * @param  {array}  accessories  Tablica z akcesoriami
     * @param  {object} colors       Kolory konstrukcji
     * @param  {string} type         Rodzaj konfiguracji
     * @return {number}              Cena po dopłacie
     */
    @PriceFunc({
        shortName: 'lock',
        data: {
            lock: 'conf.Lock',
            system: 'conf.System',
            colors: 'conf.Colors',
            conf: 'conf.type',
            sashes: 'conf.Sashes',
            components: 'conf.Components',
        },
    })
    suppLock(
        { listPrice }: PriceElemsData,
        { lock, system, colors, conf, sashes, components }: {lock; system; colors; conf; sashes; components: Components}
    ): PriceSegment[] {
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        if(components && !components.hardware) {
            return [];
        }
        if (system?.default_market_configuration?.double_door_lock_id && sashes?.map(sash => sash.lock).length > 1) {
            const priceSegments = [];
            sashes.filter(sash => ['F', 'FF', 'OFF', 'DS', 'ODS', 'DSC', 'DRP', 'DOP', 'OF'].indexOf(sash.type.type) === -1).forEach(sash => {
                let supp = 0;
                if (
                    this.config().IccConfig.Configurators.door.version < 2
                    || (this.config().IccConfig.Configurators.door.singleFix && !sash.lock.id)
                    || (system && (system.door_type ||  system.door_hardware_type === 'hardware') && !sash.lock?.id)
                ) {
                    return [];
                }

                const field = this.priceAccessoryService.getAccessoryPriceField(colors, conf);

                if (!sash.lock.show_colors && !sash.lock.no_price) {
                    supp = Number(getListPrice(sash.lock, 'price_white', listPrice, listPriceEnableInMarket) || 0)
                    supp += Number(getListPrice(sash.lock, 'price_for_assembly', listPrice, listPriceEnableInMarket) || 0)
                } else if (!sash.lock.no_price) {
                    supp = parseFloat(getListPrice(sash.lock, field, listPrice, listPriceEnableInMarket));
                    supp += parseFloat(getListPrice(sash.lock, 'price_for_assembly', listPrice, listPriceEnableInMarket) || 0);
                } else {
                    supp = NaN;
                }
                priceSegments.push(
                    {
                        type: 'lock',
                        baseValue: !isNaN(supp) ? supp : null,
                        value: !isNaN(supp) ? supp : null,
                        valueType: 'value',
                        data: {
                            id: sash.lock.id,
                            name: sash.lock.name,
                            colorField: this.priceAccessoryService.getAccessoryPriceField(colors, conf),
                        },
                    },
                );
            });
            return priceSegments;
        } else {
            let supp = 0;
            if (
                this.config().IccConfig.Configurators.door.version < 2
                || (this.config().IccConfig.Configurators.door.singleFix && !lock.id)
                || (system && (system.door_type ||  system.door_hardware_type === 'hardware') && !lock?.id)
            ) {
                return [];
            }

            let field = this.priceAccessoryService.getAccessoryPriceField(colors, conf);

            if (!lock.show_colors && !lock.no_price) {
                supp = Number(getListPrice(lock, 'price_white', listPrice, listPriceEnableInMarket) || 0)
                supp += Number(getListPrice(lock, 'price_for_assembly', listPrice, listPriceEnableInMarket) || 0)
            } else if (!lock.no_price) {
                supp = parseFloat(getListPrice(lock, field, listPrice, listPriceEnableInMarket));
                supp += parseFloat(getListPrice(lock, 'price_for_assembly', listPrice, listPriceEnableInMarket) || 0);
            } else {
                supp = NaN;
            }
            return <PriceSegment[]>[
                {
                    type: 'lock',
                    baseValue: !isNaN(supp) ? supp : null,
                    value: !isNaN(supp) ? supp : null,
                    valueType: 'value',
                    data: {
                        id: lock.id,
                        name: lock.name,
                        colorField: this.priceAccessoryService.getAccessoryPriceField(colors, conf),
                    },
                },
            ];
        }
    }

    @PriceFunc({
        shortName: 'doorHardware',
        data: {
            doorHardware: 'conf.doorHardware',
            colors: 'conf.Colors',
            colorsData: 'data.windowHandlesColors',
            doorHandleAdjustments: 'data.doorHandleAdjustments',
            hardwareTypes: 'data.hardwareTypes',
            pullType: 'data.pullType',
            electronicLocks: 'data.electronicLocks',
            plateTypes: 'data.plateTypes',
            doorViewers: 'data.doorViewers',
            plates: 'data.plates',
            cylinders: 'data.cylinders',
            decoHingeCovers: 'data.decoHingeCovers',
            conf: 'conf.type',
            doorType: 'conf.System.door_type',
            doorHardwareType: 'conf.System.door_hardware_type',
            components: 'conf.Components',
        },
    })
    // eslint-disable-next-line max-statements
    suppDoorHardware(
        { NoPriceCauses, listPrice }: PriceElemsData,
        {
            doorHardware,
            lock,
            colors,
            conf,
            colorsData,
            doorHandleAdjustments,
            hardwareTypes,
            pullType,
            electronicLocks,
            plateTypes,
            doorViewers,
            plates,
            cylinders,
            decoHingeCovers,
            doorType,
            doorHardwareType,
            components
        }
    ): PriceSegment[] {
        let supp = 0;
        const priceSegments: PriceSegment[] = [];
        if (!this.config().IccConfig.Configurators.door.advancedHardware && !(doorType || doorHardwareType === 'hardware')) {
            return [];
        }

        const field = this.priceAccessoryService.getAccessoryPriceField(colors, conf);
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;

        const pull = core.fIdO<IccAccessoryAccessory>(pullType, doorHardware.pull);
        const pullColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.pullColor);

        if (pull && (!components || components.pull)) {
            if (
                !pull.no_price && pull.show_colors
                && Common.isObject(pull.colors_prices)
                && Common.isDefined(pull.colors_prices[pullColor?.id])
            ) {
                supp = parseFloat(getListPrice(pull, 'colors_prices', listPrice, listPriceEnableInMarket, null, pullColor?.id));
                supp += Number((getListPrice(pull, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else if (!pull.show_colors) {
                supp = Number((getListPrice(pull, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(pull, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'pull',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: pull.id,
                    name: pull.name,
                    colorField: field,
                },
            });
        }

        const doorHandleAdjustment = core.fIdO<IccAccessoryAccessory>(
            doorHandleAdjustments,
            doorHardware.doorHandleAdjustment
        );

        if (doorHandleAdjustment && (!components || components.pull || components.recessedPull)) {
            if (!doorHandleAdjustment.show_colors && !doorHandleAdjustment.no_price) {
                supp = Number((getListPrice(doorHandleAdjustment, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(doorHandleAdjustment, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (!doorHandleAdjustment.no_price) {
                supp = parseFloat(getListPrice(doorHandleAdjustment, field, listPrice, listPriceEnableInMarket));
                supp += Number((getListPrice(doorHandleAdjustment, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = NaN;
            }
            priceSegments.push({
                type: 'doorHandleAdjustment',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: doorHandleAdjustment.id,
                    name: doorHandleAdjustment.name,
                    colorField: field,
                },
            });
        }

        if(doorHardware.set === 'leverRecessedPull' && doorHardware.recessedPull && (!components || components.recessedPull)) {
            priceSegments.push({
                type: 'recessedPull',
                baseValue: Number((getListPrice(doorHardware.recessedPullColor, 'price', listPrice, listPriceEnableInMarket)) || 0),
                value: Number((getListPrice(doorHardware.recessedPullColor, 'price', listPrice, listPriceEnableInMarket)) || 0),
                valueType: 'value',
                data: {
                    id: doorHardware.recessedPull,
                    colorName: doorHardware.recessedPullColor.color.name,
                },
            });
        }
        if(components && !components.hardware) {
            return priceSegments;
        }

        const plateColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.plates.color);
        const outerPlateColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.outerPlates.color);
        const typeColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.color);
        const colorId = plateColor && plateColor.id || typeColor && typeColor.id;
        const outerPlateColorId = outerPlateColor && outerPlateColor.id || typeColor && typeColor.id;
        const mainCylinderColor = core.fIdO<IccAccessoryAccessory>(
            colorsData,
            doorHardware.cylinders?.mainLockColor || doorHardware.cylinders?.color
        );
        const additionalCylinderColor = core.fIdO<IccAccessoryAccessory>(
            colorsData,
            doorHardware.cylinders?.additionalLockColor || doorHardware.cylinders?.color
        );
        const mainCylinderColorId = mainCylinderColor?.id || colorId;
        const additionalCylinderColorId = additionalCylinderColor?.id || colorId;

        const electronicLock = core.fIdO<IccAccessoryAccessory>(
            electronicLocks,
            doorHardware.electronicLock?.id
        );

        let electronicLockColorPrice;
        if (electronicLock?.colors_ids !== null && electronicLock?.colors_prices) {
            const electronicLockColor = doorHardware.electronicLock.color;

            electronicLockColorPrice = getListPrice(electronicLock, 'colors_prices', listPrice, listPriceEnableInMarket, null, electronicLockColor);
        }

        if (electronicLock) {
            if (!electronicLock.show_colors && !electronicLock.no_price){
                supp = Number((getListPrice(electronicLock, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(electronicLock, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (!electronicLock.no_price) {
                supp = parseFloat(electronicLockColorPrice);
                supp += Number((getListPrice(electronicLock, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = NaN;
            }
            priceSegments.push({
                type: 'electronicLock',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: electronicLock.id,
                    name: electronicLock.name,
                    colorField: field,
                },
            });
        }
        const plateType = core.fIdO<IccAccessoryAccessory>(plateTypes, doorHardware.plateType);
        if (plateType) {
            if (!plateType.show_colors && !plateType.no_price) {
                supp = Number((getListPrice(plateType, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(plateType, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (!plateType.no_price) {
                supp = parseFloat(getListPrice(plateType, field, listPrice, listPriceEnableInMarket));
                supp += Number((getListPrice(plateType, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else{
                supp = NaN;
            }
            priceSegments.push({
                type: 'plateType',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: plateType.id,
                    name: plateType.name,
                    colorField: field,
                },
            });
        }
        const doorViewer = core.fIdO<IccAccessoryAccessory>(doorViewers, doorHardware.doorViewer);
        if (doorViewer) {
            const doorViewerColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.doorViewerColor);
            
            if (!doorViewer.show_colors && !doorViewer.no_price) {
                supp = Number((getListPrice(doorViewer, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(doorViewer, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (
                !doorViewer.no_price
                && Common.isObject(doorViewer.colors_prices)
                && doorViewerColor
                && Common.isDefined(doorViewer.colors_prices[doorViewerColor.id])
            ) {
                supp = parseFloat(getListPrice(doorViewer, 'colors_prices', listPrice, listPriceEnableInMarket, null, doorViewerColor.id));
                supp += Number((getListPrice(doorViewer, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else if (!doorViewer.no_price) {
                supp = parseFloat(getListPrice(doorViewer, field, listPrice, listPriceEnableInMarket));
                supp += Number((getListPrice(doorViewer, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = NaN;
            }
            priceSegments.push({
                type: 'doorViewer',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: doorViewer.id,
                    name: doorViewer.name,
                    colorField: field,
                },
            });
        }
        const additionalLockPlate = core.fIdO<IccAccessoryAccessory>(
            plates,
            doorHardware.plates.additionalLock
        );
        if (additionalLockPlate) {
            if (!additionalLockPlate.show_colors && !additionalLockPlate.no_price) {
                supp = Number((getListPrice(additionalLockPlate, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(additionalLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (
                !additionalLockPlate.no_price
                && Common.isObject(additionalLockPlate.colors_prices)
                && Common.isDefined(additionalLockPlate.colors_prices[plateColor?.id])
            ) {
                supp = parseFloat(getListPrice(additionalLockPlate, 'colors_prices', listPrice, listPriceEnableInMarket, null, plateColor?.id));
                supp += Number((getListPrice(additionalLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'additionalLockPlate',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: additionalLockPlate.id,
                    name: additionalLockPlate.name,
                    colorField: field,
                },
            });
        }

        const outerAdditionalLockPlate = core.fIdO<IccAccessoryAccessory>(
            plates,
            doorHardware.outerPlates.additionalLock
        );
        if (outerAdditionalLockPlate) {
            if (!outerAdditionalLockPlate.show_colors && !outerAdditionalLockPlate.no_price) {
                supp = Number((getListPrice(outerAdditionalLockPlate, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(outerAdditionalLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (
                !outerAdditionalLockPlate.no_price
                && Common.isObject(outerAdditionalLockPlate.colors_prices)
                && Common.isDefined(outerAdditionalLockPlate.colors_prices[plateColor?.id])
            ) {
                supp = parseFloat(getListPrice(outerAdditionalLockPlate, 'colors_prices', listPrice, listPriceEnableInMarket, null, plateColor?.id));
                supp += Number((getListPrice(outerAdditionalLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'outerAdditionalLockPlate',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: outerAdditionalLockPlate.id,
                    name: outerAdditionalLockPlate.name,
                    colorField: field,
                },
            });
        }
        const mainLockCylinder = core.fIdO<IccAccessoryAccessory>(
            cylinders,
            doorHardware.cylinders.mainLock
        );
        if (mainLockCylinder) {
            const hasMainColorsPrices = Common.isObject(mainLockCylinder.colors_prices);
            const hasMainResolvedColorPrice = hasMainColorsPrices
                && Common.isDefined(mainLockCylinder.colors_prices[mainCylinderColorId]);
            if (!mainLockCylinder.show_colors && !mainLockCylinder.no_price) {
                supp = Number((getListPrice(mainLockCylinder, 'price_white', listPrice, listPriceEnableInMarket)) || 0)
                supp += Number((getListPrice(mainLockCylinder, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0)
            } else if (
                !mainLockCylinder.no_price
                && hasMainColorsPrices
                && hasMainResolvedColorPrice
            ) {
                supp = parseFloat(getListPrice(mainLockCylinder, 'colors_prices', listPrice, listPriceEnableInMarket, null, mainCylinderColorId));
                supp += Number((getListPrice(mainLockCylinder, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'mainLockCylinder',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: mainLockCylinder.id,
                    name: mainLockCylinder.name,
                    colorField: field,
                },
            });
        }

        const keyForMainLockCylinder = core.fIdO<IccAccessoryAccessory>(
            cylinders,
            doorHardware.cylinders.mainLock
        );
        if (keyForMainLockCylinder?.additional_key_for_the_cylinder) {
            if (
                !keyForMainLockCylinder.no_price
            ) {
                supp =
                    Number(doorHardware.cylinders.keyPriceForMainLock || 0) *
                    Number(doorHardware.cylinders.keyQuantityForMainLock || 0);
            } else {
                supp = null;
            }

            if (supp > 0) {
                priceSegments.push({
                    type: 'keyForMainLockCylinder',
                    baseValue: supp,
                    value: supp,
                    valueType: 'value',
                    data: {
                        name: this.translateService.instant(
                            'INTERFACE|Dodatkowe klucze do zamka głównego'
                        ),
                    },
                });
            }
        }

        const additionalLockCylinder = core.fIdO<IccAccessoryAccessory>(
            cylinders,
            doorHardware.cylinders.additionalLock
        );
        if (additionalLockCylinder) {
            const hasAdditionalColorsPrices = Common.isObject(additionalLockCylinder.colors_prices);
            const hasAdditionalResolvedColorPrice = hasAdditionalColorsPrices
                && Common.isDefined(additionalLockCylinder.colors_prices[additionalCylinderColorId]);
            if (!additionalLockCylinder.show_colors && !additionalLockCylinder.no_price) {
                supp = Number((getListPrice(additionalLockCylinder, 'price_white', listPrice, listPriceEnableInMarket)) || 0);
                supp += Number((getListPrice(additionalLockCylinder, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else if (
                !additionalLockCylinder.no_price
                && hasAdditionalColorsPrices
                && hasAdditionalResolvedColorPrice
            ) {
                supp = parseFloat(getListPrice(additionalLockCylinder, 'colors_prices', listPrice, listPriceEnableInMarket, null, additionalCylinderColorId));
                supp += Number((getListPrice(additionalLockCylinder, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'additionalLockCylinder',
                baseValue: !isNaN(supp) ? supp : null,
                value: !isNaN(supp) ? supp : null,
                valueType: 'value',
                data: {
                    id: additionalLockCylinder.id,
                    name: additionalLockCylinder.name,
                    colorField: field,
                },
            });
        }

        const keyForAdditionalLockCylinder = core.fIdO<IccAccessoryAccessory>(
            cylinders,
            doorHardware.cylinders.additionalLock
        );
        if (keyForAdditionalLockCylinder?.additional_key_for_the_cylinder) {
            if (!keyForAdditionalLockCylinder.no_price) {
                supp =
                    Number(doorHardware.cylinders.keyPriceForAdditionalLock || 0) *
                    Number(doorHardware.cylinders.keyQuantityForAdditionalLock || 0);
            } else {
                supp = null;
            }

            if (supp > 0) {
                priceSegments.push({
                    type: 'keyForAdditionalLockCylinder',
                    baseValue: supp,
                    value: supp,
                    valueType: 'value',
                    data: {
                        name: this.translateService.instant(
                            'INTERFACE|Dodatkowe klucze do zamka dodatkowego'
                        ),
                    },
                });
            }
        }

        const type = core.fIdO<IccAccessoryAccessory>(hardwareTypes, doorHardware.type);

        if (Common.isObject(type)) {
            if (
                !type.no_price
                && Common.isObject(type.colors_prices)
                && Common.isDefined(type.colors_prices[colorId])
            ) {
                supp = parseFloat(getListPrice(type, 'colors_prices', listPrice, listPriceEnableInMarket, null, colorId));
                supp += Number((getListPrice(type, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'hardwareType',
                baseValue: supp,
                value: supp,
                valueType: 'value',
                data: {
                    id: type.id,
                    name: type.name,
                    colorName: typeColor && typeColor.name,
                },
            });
        }

        const mainLockPlate = core.fIdO<IccAccessoryAccessory>(
            plates,
            doorHardware.plates.mainLock
        );

        if (Common.isObject(mainLockPlate)) {
            if (!mainLockPlate.show_colors && !mainLockPlate.no_price) {
                supp = Number((getListPrice(mainLockPlate, 'price_white', listPrice, listPriceEnableInMarket)) || 0);
                supp += Number((getListPrice(mainLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else if (
                !mainLockPlate.no_price
                && Common.isObject(mainLockPlate.colors_prices)
                && Common.isDefined(mainLockPlate.colors_prices[plateColor?.id])
            ) {
                supp = parseFloat(getListPrice(mainLockPlate, 'colors_prices', listPrice, listPriceEnableInMarket, null, plateColor?.id));
                supp += Number((getListPrice(mainLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'mainLockPlate',
                baseValue: supp,
                value: supp,
                valueType: 'value',
                data: {
                    id: mainLockPlate.id,
                    name: mainLockPlate.name,
                    colorName: plateColor && plateColor.name,
                },
            });
        }

        const outerMainLockPlate = core.fIdO<IccAccessoryAccessory>(
            plates,
            doorHardware.outerPlates.mainLock
        );

        if (Common.isObject(outerMainLockPlate)) {
            if (!outerMainLockPlate.show_colors && !outerMainLockPlate.no_price) {
                supp = Number((getListPrice(outerMainLockPlate, 'price_white', listPrice, listPriceEnableInMarket)) || 0);
                supp += Number((getListPrice(outerMainLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else if (
                !outerMainLockPlate.no_price
                && Common.isObject(outerMainLockPlate.colors_prices)
                && Common.isDefined(outerMainLockPlate.colors_prices[outerPlateColor?.id])
            ) {
                supp = parseFloat(getListPrice(outerMainLockPlate, 'colors_prices', listPrice, listPriceEnableInMarket, null, outerPlateColor?.id));
                supp += Number((getListPrice(outerMainLockPlate, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'outerMainLockPlate',
                baseValue: supp,
                value: supp,
                valueType: 'value',
                data: {
                    id: outerMainLockPlate.id,
                    name: outerMainLockPlate.name,
                    colorName: outerPlateColor && outerPlateColor.name,
                },
            });
        }

        const decorativeHingeCovers = core.fIdO<IccAccessoryAccessory>(
            decoHingeCovers,
            doorHardware.decorativeHingeCovers
        );
        const decorativeHingeCoversColor = core.fIdO<IccAccessoryAccessory>(colorsData, doorHardware.decorativeHingeCoversColor);
        if (Common.isObject(decorativeHingeCovers)) {
            if (
                Common.isObject(decorativeHingeCovers.colors_prices)
                && Common.isObject(decorativeHingeCoversColor)
                && Common.isDefined(decorativeHingeCovers.colors_prices[decorativeHingeCoversColor.id])
            ) {
                supp = parseFloat(getListPrice(decorativeHingeCovers, 'colors_prices', listPrice, listPriceEnableInMarket, null, decorativeHingeCoversColor?.id));
                supp += Number((getListPrice(decorativeHingeCovers, 'price_for_assembly', listPrice, listPriceEnableInMarket)) || 0);
            } else {
                supp = null;
            }
            priceSegments.push({
                type: 'decorativeHingeCovers',
                baseValue: supp,
                value: supp,
                valueType: 'value',
                data: {
                    id: decorativeHingeCovers.id,
                    name: decorativeHingeCovers.name,
                    colorName: decorativeHingeCoversColor && decorativeHingeCoversColor.name,
                },
            });
        }

        return priceSegments;
    }
}
